import api from './api/client'

export type ReportId = 'owners' | 'legal-names' | 'statuses' | 'last-processed' | 'new-accounts'

export interface ReportMerchant {
  id: string
  dba_name: string
  legal_name?: string | null
  status: string
}

export interface OwnerReportRow {
  owner_name: string
  merchant_count: number
  merchants: ReportMerchant[]
}

export interface LegalNameReportRow {
  legal_name: string | null
  merchant_count: number
  merchants: Array<Pick<ReportMerchant, 'id' | 'dba_name' | 'status'>>
}

export interface StatusCount {
  status: 'active' | 'suspended' | 'terminated'
  count: number
  merchants_href: string
}

export interface StatusReportRow {
  id: string
  dba_name: string
  legal_name: string | null
  status: string
}

export interface LastProcessedReportRow extends StatusReportRow {
  last_batch_at: string | null
  last_batch_state: 'not_synced' | 'synced'
  last_transaction_sync_at: string | null
}

export interface NewAccountReportRow {
  period: string
  rep_code: string | null
  rep_name: string | null
  user: { id: string; name: string } | null
  merchant_count: number
}

export interface ReportEnvelope<T> {
  report: ReportId
  data: T[]
  current_page: number
  last_page: number
  per_page: number
  total: number
  from: number | null
  to: number | null
  sort_by: string
  sort_dir: 'asc' | 'desc'
  filters: Record<string, string | number | null>
  can_export: boolean
  counts?: StatusCount[]
  scope?: 'portfolio' | 'self'
  group_by?: 'month' | 'year'
}

export type ReportParams = Record<string, string | number | undefined>

export async function fetchReport<T>(report: ReportId, params: ReportParams): Promise<ReportEnvelope<T>> {
  const response = await api.get<ReportEnvelope<T>>(`/reports/${report}`, {
    params: compact(params),
  })
  return response.data
}

export async function exportReport(report: ReportId, params: ReportParams): Promise<void> {
  const response = await api.get(`/reports/${report}/export`, {
    params: compact(params),
    responseType: 'blob',
  })
  const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  const disposition = String(response.headers['content-disposition'] ?? '')
  const filename = disposition.match(/filename="?([^"]+)"?/)?.[1] ?? `${report}.csv`
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

function compact(params: ReportParams): Record<string, string | number> {
  const next: Record<string, string | number> = {}
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') {
      next[key] = value
    }
  }
  return next
}
